import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAgency, updateAgency } from "../../../data";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AppLayout } from "../shared/layout";
import { ClientCard } from "@/components";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Building2,
  Mail,
  Phone,
  CreditCard,
  MapPin,
  Briefcase,
  User,
  Users,
  Percent,
  FileText,
} from "lucide-react";

export function AgencyUpdateScreen() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("details");

  const pageSettings = {
    title: "Agency Detail",
    actions: [
      {
        name: "Go To List",
        color: "blue",
        icon: "add-icon",
        onClick: () => {
          navigate("/agencies");
        },
      },
    ],
  };

  const { id } = useParams();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const { data, isLoading, error } = useQuery({
    queryKey: ["agency", id],
    queryFn: async () => await getAgency(id),
  });

  const mutation = useMutation({
    mutationKey: ["updateAgency", id],
    mutationFn: (data) => {
      return updateAgency(data.id, data.data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["agency", id] });
    },
  });

  const onSubmit = async (data) => {
    mutation.mutate({
      id: id,
      data: {
        email: data.email?.trim(),
        name: data.name?.trim(),
        phoneNumber: data.phoneNumber?.trim(),
        billingName: data.billingName?.trim(),
        bankName: data.bankName?.trim(),
        IBAN: data.IBAN?.trim(),
        SWIFT: data.SWIFT?.trim(),
        country: data.country?.trim(),
        currency: data.currency?.trim(),
        lucaId: data.lucaId?.trim(),
        lucaAccountName: data.lucaAccountName?.trim(),
        newTvAccountManager: data.newTvAccountManager?.trim(),
        agencyCommissionPercentage: data.agencyCommissionPercentage ? parseFloat(data.agencyCommissionPercentage) : null,
        isPrepareTheInvoiceCorporate: data.isPrepareTheInvoiceCorporate || false,
      },
    });
  };

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div>Loading...</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={pageSettings.title + " - " + data.data.name}
      actions={pageSettings.actions}
    >
      <div className="px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="details" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Agency Details
            </TabsTrigger>
            <TabsTrigger value="clients" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Clients ({data.data.clients?.length || 0})
            </TabsTrigger>
          </TabsList>

          {/* Agency Details Tab */}
          <TabsContent value="details">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Basic Information */}
                  <Card>
                    <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Building2 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                        Basic Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div>
                        <Label htmlFor="name" className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          Agency Name *
                        </Label>
                        <Input
                          id="name"
                          type="text"
                          placeholder="Enter agency name"
                          className="mt-1.5"
                          {...register("name", {
                            required: "Name is required",
                          })}
                          defaultValue={data.data.name}
                        />
                        {errors.name && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.name.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="email" className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Email *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="agency@example.com"
                          className="mt-1.5"
                          {...register("email", {
                            required: "Email is required",
                            pattern: {
                              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                              message: "Invalid email format",
                            },
                          })}
                          defaultValue={data.data.email}
                        />
                        {errors.email && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.email.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label
                          htmlFor="phoneNumber"
                          className="flex items-center gap-2"
                        >
                          <Phone className="h-4 w-4" />
                          Phone Number *
                        </Label>
                        <Input
                          id="phoneNumber"
                          type="text"
                          placeholder="+1 234 567 8900"
                          className="mt-1.5"
                          {...register("phoneNumber", {
                            required: "Phone Number is required",
                          })}
                          defaultValue={data.data.phoneNumber}
                        />
                        {errors.phoneNumber && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.phoneNumber.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label
                          htmlFor="billingName"
                          className="flex items-center gap-2"
                        >
                          <User className="h-4 w-4" />
                          Billing Name *
                        </Label>
                        <Input
                          id="billingName"
                          type="text"
                          placeholder="Billing name"
                          className="mt-1.5"
                          {...register("billingName", {
                            required: "Billing Name is required",
                          })}
                          defaultValue={data.data.billingName}
                        />
                        {errors.billingName && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.billingName.message}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Location & Manager */}
                  <Card>
                    <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <MapPin className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        Location & Management
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div>
                        <Label htmlFor="country" className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          Country
                        </Label>
                        <Input
                          id="country"
                          type="text"
                          placeholder="Country"
                          className="mt-1.5"
                          {...register("country")}
                          defaultValue={data.data.country}
                        />
                      </div>

                      <div>
                        <Label htmlFor="currency" className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Currency
                        </Label>
                        <Input
                          id="currency"
                          type="text"
                          placeholder="EUR, USD, etc."
                          className="mt-1.5"
                          {...register("currency")}
                          defaultValue={data.data.currency}
                        />
                      </div>

                      <div>
                        <Label
                          htmlFor="newTvAccountManager"
                          className="flex items-center gap-2"
                        >
                          <User className="h-4 w-4" />
                          New TV Account Manager
                        </Label>
                        <Input
                          id="newTvAccountManager"
                          type="text"
                          placeholder="Manager name"
                          className="mt-1.5"
                          {...register("newTvAccountManager")}
                          defaultValue={data.data.newTvAccountManager}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Luca Account */}
                  <Card>
                    <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Briefcase className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        Luca Account
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div>
                        <Label htmlFor="lucaId" className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4" />
                          Luca ID
                        </Label>
                        <Input
                          id="lucaId"
                          type="text"
                          placeholder="Luca ID"
                          className="mt-1.5 font-mono"
                          {...register("lucaId")}
                          defaultValue={data.data.lucaId}
                        />
                      </div>

                      <div>
                        <Label
                          htmlFor="lucaAccountName"
                          className="flex items-center gap-2"
                        >
                          <User className="h-4 w-4" />
                          Luca Account Name
                        </Label>
                        <Input
                          id="lucaAccountName"
                          type="text"
                          placeholder="Luca Account Name"
                          className="mt-1.5"
                          {...register("lucaAccountName")}
                          defaultValue={data.data.lucaAccountName}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Commission Settings */}
                  <Card>
                    <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Percent className="h-5 w-5 text-green-600 dark:text-green-400" />
                        Commission Settings
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div>
                        <Label
                          htmlFor="agencyCommissionPercentage"
                          className="flex items-center gap-2"
                        >
                          <Percent className="h-4 w-4" />
                          Agency Commission Percentage (%)
                        </Label>
                        <Input
                          id="agencyCommissionPercentage"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder="e.g. 10"
                          className="mt-1.5"
                          {...register("agencyCommissionPercentage")}
                          defaultValue={data.data.agencyCommissionPercentage}
                        />
                        {errors.agencyCommissionPercentage && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.agencyCommissionPercentage.message}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="isPrepareTheInvoiceCorporate"
                          name="isPrepareTheInvoiceCorporate"
                          onCheckedChange={(checked) => {
                            setValue("isPrepareTheInvoiceCorporate", checked);
                          }}
                          defaultChecked={data.data.isPrepareTheInvoiceCorporate || false}
                        />
                        <Label
                          htmlFor="isPrepareTheInvoiceCorporate"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                        >
                          <FileText className="h-4 w-4" />
                          Prepare Invoice Corporate (Kurumsal Fatura)
                        </Label>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Banking Details */}
                  <Card>
                    <CardHeader className="border-b bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <CreditCard className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                        Banking Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div>
                        <Label htmlFor="bankName" className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          Bank Name *
                        </Label>
                        <Input
                          id="bankName"
                          type="text"
                          placeholder="Bank name"
                          className="mt-1.5"
                          {...register("bankName", {
                            required: "Bank Name is required",
                          })}
                          defaultValue={data.data.bankName}
                        />
                        {errors.bankName && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.bankName.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="IBAN" className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          IBAN *
                        </Label>
                        <Input
                          id="IBAN"
                          type="text"
                          placeholder="IBAN"
                          className="mt-1.5 font-mono"
                          {...register("IBAN", {
                            required: "IBAN is required",
                          })}
                          defaultValue={data.data.IBAN}
                        />
                        {errors.IBAN && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.IBAN.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="SWIFT" className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          SWIFT *
                        </Label>
                        <Input
                          id="SWIFT"
                          type="text"
                          placeholder="SWIFT/BIC code"
                          className="mt-1.5 font-mono"
                          {...register("SWIFT", {
                            required: "SWIFT is required",
                          })}
                          defaultValue={data.data.SWIFT}
                        />
                        {errors.SWIFT && (
                          <p className="text-sm text-destructive mt-1">
                            {errors.SWIFT.message}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Submit Button */}
              <div className="mt-8 flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/agencies")}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
                >
                  Update Agency
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients">
            <div className="space-y-6">
              {/* Statistics Card */}
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Total Clients
                      </p>
                      <p className="text-3xl font-bold text-foreground mt-1">
                        {data.data.clients?.length || 0}
                      </p>
                    </div>
                    <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Clients List */}
              {data.data.clients && data.data.clients.length > 0 ? (
                <Card>
                  <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      All Clients
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {data.data.clients.map((client) => (
                        <ClientCard
                          key={client.id}
                          name={client.name}
                          email={client.email}
                          socialMediaAccounts={client.socialMediaAccounts}
                          editClick={() => {
                            navigate(`/clients/${client.id}`);
                          }}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-medium text-muted-foreground">
                      No clients found for this agency
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
